<template>
    <b-modal
        size="md"
        :visible="show"
        @hidden="hideEvents"
        :id="modalId"
        :title="title"
        class="note-modal"
        :scrollable=false
    >
        <vue-element-loading :active="loading"></vue-element-loading>
        <b-form>

            <vue-dropzone
                    v-if="fileUrl"
                    id="fileDrop"
                    ref="dropzone"
                    :options="dropOptions"
                    @vdropzone-sending="attachParams"
                    @vdropzone-success-multiple="onFileSuccess"
                    @vdropzone-error="onFileError"
            >
            </vue-dropzone>
        </b-form>
        <r-form
                class="file-type-form"
                :column-size="1"
                :crud="true"
                :validators="validators"
                :showHeader="false"
                :showFooter="false"
                ref="form"
                v-model="form"
                :disabled="false"
        >
        </r-form>
        <template slot="modal-footer">
            <b-button
                    variant="outline-secondary"
                    class="cancel"
                    @click="hideModal">
                Cancel
                <font-awesome-icon
                        :icon="['far', 'times']">
                </font-awesome-icon>
            </b-button>
            <b-button
                    variant="yellow"
                    @click="onUploadFiles"
                    :disabled="loading"
            >
                {{ buttonText }}
                <font-awesome-icon
                        class="ml-3"
                        :icon="['fal', 'upload']"
                >
                </font-awesome-icon>
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import ModalMixin from '@/components/Modal/ModalMixin';
import FileDropMixin from '@/components/Form/FileDropMixin';
import dayjs from 'dayjs';
import { form as PropertyFileTypeForm, validators as PropertyFileTypeValidators } from '@/services/Property/PropertyFileTypeForm';
import RForm from '@/components/Form/RiskhubForm';
import PropertyDocument from '../../../services/Property/PropertyDocument';
import { cloneDeep } from 'lodash';

export default {
  name: 'Property-File-Modal',
  components: {
    PropertyFileTypeForm,
    PropertyFileTypeValidators,
    RForm,
  },
  props: {
    show: {
      default: false,
    },
    title: {
      default: 'Upload file',
    },
    busy: {
      default: false,
    },
    buttonText: {
      default: 'Upload',
    },
    errorMessage: {
      type: String,
    },
  },
  mixins: [FileDropMixin, ModalMixin],
  data() {
    return {
      modalId: 'propertyFileModal',
      dropOptions: {
        maxFiles: 1,
        dictDefaultMessage: 'Drop file here to upload, or click to <span class="underline">select</span> a file to upload.',
        maxFilesize: 40, // MB
        timeout: 300000,
      },
      validators: cloneDeep(PropertyFileTypeValidators),
      form: PropertyFileTypeForm(),
      expiryDate: '',
      issueDate: '',
      propertyFileTypeId: '',
    };
  },
  watch: {
    busy: {
      handler(newBusy) {
        this.loading = newBusy;
      },
      immediate: true,
    },
    'form.propertyFileTypeId.value.propertyFileTypeId': {
      async handler() {
        await this.updateValidators();
      },
    },
  },
  methods: {
    async onUploadFiles() {
      this.loading = true;

      // File route, post via dropzone
      if (!this.fileUrl || !this.getFiles().length) {
        this.$notify({
          title: 'No files selected',
          text: 'Please select at least one file.',
          type: 'warn',
        });

        this.loading = false;

        return;
      }

      const { form } = this;

      // Because the it is not mandatory to have a file type, we default
      // isValidated to true
      let isValidated = true;

      if (form.propertyFileTypeId.value) {
        this.propertyFileTypeId = form.propertyFileTypeId.value.propertyFileTypeId;

        this.expiryDate = form.propertyFileTypeId.value.hasExpiryDate
          ? form.expiryDate.value : 0;

        this.issueDate = form.propertyFileTypeId.value.hasExpiryDate
          ? form.issueDate.value : 0;

        isValidated = this.$refs.form.isValidated();
      }

      if (!isValidated) {
        this.$notify({
          title: 'Form invalid',
          text: 'Please complete the form before submitting',
          type: 'error',
        });

        this.loading = false;

        return;
      }

      this.processFiles();
    },

    hideModalEvents() {
      this.$emit('update:show');
    },

    onFileSuccess(file, response) {
      if (this.$refs.form.isValidated()) {
        this.addFileType(response);
      } else {
        this.$emit('file-sent', response.meta.success);
        this.loading = false;
        this.hideModal();
      }
    },

    async addFileType(response) {
      const expiryDate = this.expiryDate
        ? dayjs(this.expiryDate).format('YYYY-MM-DD')
        : '';

      const issueDate = this.issueDate
        ? dayjs(this.issueDate).format('YYYY-MM-DD')
        : '';

      try {
        const propertyDocumentIds = response.data;

        await PropertyDocument.updateType(
          this.propertyId,
          [propertyDocumentIds],
          expiryDate,
          issueDate,
          this.propertyFileTypeId,
        );

        this.$notify({
          title: 'File type updated',
          message: 'File type has been successfully updated',
          type: 'success',
        });

        this.$emit('file-type-updated', true);
      } catch (e) {
        this.$notify({
          title: 'Error updating file type',
          message: 'File type has not been updated',
          type: 'error',
        });
      } finally {
        this.$emit('file-sent', response.meta.success);
        this.loading = false;
        this.hideModal();
      }
    },

    showErrorRequired() {
      const title = 'File or note is required';
      const text = '';
      const type = 'error';

      this.$notify({
        duration: 7000,
        title,
        text,
        type,
      });
    },

    updateValidators() {
      const { form } = this;

      this.validators = cloneDeep(PropertyFileTypeValidators);

      // If the selected type does not require an expiry date, we remove it
      // it from the validator.
      if (form.propertyFileTypeId.value) {
        if (!form.propertyFileTypeId.value.hasExpiryDate) {
          this.validators.expiryDate = {
            required: false,
          };
        }

        if (!form.propertyFileTypeId.value.hasIssueDate) {
          this.validators.issueDate = {
            required: false,
          };
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
    .file-type-form {
        z-index: 30;
    }
</style>
